import { useEffect, useMemo, useRef, useState } from 'react'
import type { AppWorkerClient } from '../../../data/AppWorkerClient'
import type {
  CfoInsightsResponse,
  InsightCard,
  MetricComparisonKind,
  MetricComparisonResponse,
  QuickBooksCashPlanningResponse,
  QuickBooksCashPlanningRow,
  QuickBooksCashPlanningWeekSummary,
  QuickBooksFpaMlCashActionResponse,
  ServerApiClient,
} from '../../../data/ServerApiClient'
import { CfoInteriorPage } from './CfoInteriorPage'
import { useCfoInlineGlDetail } from './CfoGlDetailModal'
import { CfoInsightCards } from './CfoInsightCards'
import { TrendLine, type TrendPoint } from './charts'
import {
  CfoAiRead,
  CfoChartSkeleton,
  CfoControlRow,
} from './CfoPagePrimitives'
import { startCfoCheckout } from './cfoBilling'
import { buildCfoDuckDbOpts } from './cfoDuckDb'
import {
  formatAsOf,
  cfoCurrencyPresentation,
  formatCurrencyCompact,
  formatSignedCurrency,
  trimStr,
} from './cfoFormat'
import { CFO_SURFACES } from './cfoTelemetry'
import { cfoProFeaturesAvailable, useCfoBootstrap } from './useCfoBootstrap'
import type { CfoShellSelectOption } from './CfoPageShell'
import type { CfoNavProfile } from './cfoNav'
import type { CfoRuntimeProfile } from './cfoRuntimeProfile'
import { buildCfoInstantCardFromContext } from './analysis/instantCards'
import type { CfoInstantCard } from './analysis/types'
import { buildCashTableModel, type CashTableCategoryKey } from './cfoCashTableModel'
import { buildTroughCallouts } from './cfoCashTroughCopy'
import { Cfo13WeekCashTable } from './Cfo13WeekCashTable'
import { CfoCashWeekDrill } from './CfoCashWeekDrill'
import { CfoCashAddItem, type CfoCashAddItemValue } from './CfoCashAddItem'
import CfoCashTieOutStrip from './CfoCashTieOutStrip'
import CfoCashAvailableStrip from './CfoCashAvailableStrip'
import {
  expectationSourceTypeFor,
  optimisticExpectationRow,
  projectCashPlanningExpectation,
  reconcileCashPlanningExpectation,
  type CashPlanningMove,
} from './cfoCashPlanProjection'
import { demoScenarioKey, isDemoSession } from '../../../data/demoSession'

const SURFACE = CFO_SURFACES.cash
const CASH_REQUIRED_COMPARISONS: MetricComparisonKind[] = [
  'mom',
  'l3m_vs_prior3m',
  'ytd',
  'yoy',
]
const CASH_HISTORY_OPTIONS = [0, 4, 8, 13, 26] as const

const demoFloorStorageKey = (workbookId: string) =>
  `fusegrid_demo_floor_${workbookId}`

const loadDemoFloor = (workbookId: string, seeded: number | null): number | null => {
  try {
    const key = demoFloorStorageKey(workbookId)
    const stored = window.localStorage.getItem(key)
    if (stored === 'null') return null
    if (stored != null) {
      const value = Number(stored)
      if (Number.isFinite(value)) return value
    }
    window.localStorage.setItem(key, seeded == null ? 'null' : String(seeded))
  } catch {
    // Storage can be unavailable in privacy modes; keep the in-memory floor.
  }
  return seeded
}

export type CfoCashPageProps = {
  appApi: ServerApiClient
  workerClient?: AppWorkerClient | null
  workbookId: string | null
  selectedEndPeriod?: string | null
  onSelectedEndPeriodChange?: (value: string | null) => void
  periodOptions?: CfoShellSelectOption[]
  comparisonKind?: MetricComparisonKind
  onComparisonKindChange?: (value: MetricComparisonKind) => void
  comparisonOptions?: CfoShellSelectOption[]
  onNavSelect: (navId: string) => void
  onOpenClassic: () => void
  classicEscapes?: boolean
  navProfile?: CfoNavProfile
  runtimeProfile?: CfoRuntimeProfile
}

const findMetric = (cmp: MetricComparisonResponse | null, id: string) =>
  cmp?.metrics.find((m) => m.id === id) || null

const finiteNumber = (value: unknown): number | null => {
  const next = Number(value)
  return Number.isFinite(next) ? next : null
}

const trendFromCashPlanningWeeks = (
  weeks: QuickBooksCashPlanningWeekSummary[] | null | undefined,
): {
  points: TrendPoint[]
  projectedDelta: number | null
  openingCash: number | null
} => {
  // Closed weeks (the plan-vs-actual record) are excluded here: "now" must be
  // today's opening cash, not the opening of a reviewed week four weeks back.
  const sorted = [...(weeks || [])]
    .filter((week) => week.week_status !== 'closed')
    .sort((a, b) => a.week_start.localeCompare(b.week_start))
  if (!sorted.length)
    return { points: [], projectedDelta: null, openingCash: null }
  const first = sorted[0]
  const explicitOpening = finiteNumber(first.opening_cash)
  const firstEnding = finiteNumber(first.ending_cash)
  const firstNet = finiteNumber(first.net_cash_change)
  const openingCash =
    explicitOpening ??
    (firstEnding != null && firstNet != null ? firstEnding - firstNet : null)
  if (openingCash == null)
    return { points: [], projectedDelta: null, openingCash: null }
  const points: TrendPoint[] = [{ label: 'now', value: openingCash }]
  for (const week of sorted) {
    const endingCash = finiteNumber(week.ending_cash)
    if (endingCash == null) continue
    points.push({ label: week.week_start, value: endingCash })
  }
  if (points.length < 2)
    return { points: [], projectedDelta: null, openingCash }
  return {
    points,
    projectedDelta: points[points.length - 1].value - points[0].value,
    openingCash,
  }
}

const cashSourceFingerprint = (
  comparisons: MetricComparisonResponse | null,
  cashPlanning: QuickBooksCashPlanningResponse | null,
): string =>
  [
    SURFACE,
    trimStr(comparisons?.signature || ''),
    trimStr(comparisons?.as_of || ''),
    trimStr(cashPlanning?.scenario_key || 'base'),
    cashPlanning?.weeks?.length || 0,
    cashPlanning?.weeks
      ?.slice(0, 2)
      .map(
        (week) => `${week.week_start}:${finiteNumber(week.ending_cash) ?? 0}`,
      )
      .join('|') || '',
    cashPlanning?.weeks
      ?.slice(-2)
      .map(
        (week) => `${week.week_start}:${finiteNumber(week.ending_cash) ?? 0}`,
      )
      .join('|') || '',
  ].join(':')

const cashMetricTrend = (
  metric: NonNullable<ReturnType<typeof findMetric>>,
  fallbackTrend: { points: TrendPoint[]; projectedDelta: number | null },
) => {
  const metricPoints = (metric.trend || []).slice(-12).map((point) => {
    const value = finiteNumber(point.value)
    return {
      label:
        trimStr(point.label) ||
        formatAsOf(point.period).replace(/\s+\d{4}$/, ''),
      period: trimStr(point.period) || null,
      rawValue: value,
      value: value == null ? '-' : formatCurrencyCompact(value),
    }
  })
  if (metricPoints.length) {
    return {
      label:
        metricPoints.length >= 12
          ? '12-month trend'
          : 'Available monthly cash trend',
      points: metricPoints,
      unavailable_reason:
        metricPoints.length >= 12
          ? null
          : `Metric comparison artifact has ${metricPoints.length} monthly cash points.`,
    }
  }
  const forecastPoints = (fallbackTrend.points || []).map((point) => ({
    label: point.label,
    period: point.label,
    rawValue: finiteNumber(point.value),
    value: formatCurrencyCompact(finiteNumber(point.value)),
  }))
  return {
    label: '13-week cash forecast',
    points: forecastPoints,
    unavailable_reason: forecastPoints.length
      ? 'Monthly cash trend was not present; showing the current 13-week cash forecast.'
      : 'Cash trend was not included in this page fact yet.',
  }
}

const cashComparisonRows = (
  metric: NonNullable<ReturnType<typeof findMetric>>,
) => {
  const byKind = new Map(
    metric.comparisons.map((comparison) => [comparison.kind, comparison]),
  )
  const row = (label: string, kind: MetricComparisonKind) => {
    const comparison = byKind.get(kind)
    const current = finiteNumber(comparison?.current_value)
    const prior = finiteNumber(comparison?.prior_value)
    const delta = current != null && prior != null ? current - prior : null
    const change =
      comparison?.delta_pct != null && Number.isFinite(comparison.delta_pct)
        ? `${comparison.delta_pct > 0 ? '+' : comparison.delta_pct < 0 ? '-' : ''}${Math.abs(comparison.delta_pct * 100).toFixed(1)}%`
        : delta == null
          ? '-'
          : formatSignedCurrency(delta)
    return {
      label,
      current: current == null ? '-' : formatCurrencyCompact(current),
      prior: prior == null ? '-' : formatCurrencyCompact(prior),
      change,
      tone:
        delta == null || Math.abs(delta) < 0.005
          ? 'neutral'
          : delta > 0
            ? 'good'
            : 'bad',
      unavailableReason:
        current == null || prior == null
          ? 'Not available in the cash comparison artifact.'
          : null,
    }
  }
  return [
    row('Current month', 'mom'),
    row('Last 3 months', 'l3m_vs_prior3m'),
    row('YTD', 'ytd'),
    row('Prior year', 'yoy'),
  ]
}

export const buildCashInstantCards = (args: {
  comparisons: MetricComparisonResponse | null
  cashPlanning: QuickBooksCashPlanningResponse | null
  cashActions: QuickBooksFpaMlCashActionResponse | null
  planningTrend: { points: TrendPoint[]; projectedDelta: number | null }
  asOfLabel: string
  comparisonKind: MetricComparisonKind
}): CfoInstantCard[] => {
  const {
    comparisons,
    cashPlanning,
    cashActions,
    planningTrend,
    asOfLabel,
    comparisonKind,
  } = args
  const metric =
    findMetric(comparisons, 'cash') || findMetric(comparisons, 'ending_cash')
  const currentCash =
    finiteNumber(metric?.latest_value) ??
    trendFromCashPlanningWeeks(cashPlanning?.weeks).openingCash
  if (currentCash == null || !metric) return []
  const sourceFingerprint = cashSourceFingerprint(comparisons, cashPlanning)
  const actionSummary = trimStr(cashActions?.actions?.[0]?.title || '')
  const projectedDelta = finiteNumber(planningTrend.projectedDelta)
  return [
    buildCfoInstantCardFromContext({
      context: {
        workbookId: null,
        pageId: 'cash',
        activeSection: SURFACE,
        period: comparisons?.as_of || null,
        comparison: comparisonKind,
        asOfLabel,
        metricId: metric.id,
        metricLabel: 'Cash on hand',
        sourceComponent: 'cash_position',
        sourceTables: ['quickbooks_cash_planning', 'cfo_metric_comparisons'],
        grounding: null,
        instantCard: null,
        selection: {
          selection_key: 'cash:cash_on_hand',
          value: currentCash,
          metric_delta: projectedDelta,
          source_fingerprint: sourceFingerprint,
          artifact_ids: ['cash_planning', comparisons?.signature].filter(
            Boolean,
          ),
          instant_card_trend: cashMetricTrend(metric, planningTrend),
          instant_card_comparisons: cashComparisonRows(metric),
          instant_card_insights: [
            projectedDelta != null
              ? `Cash is ${formatCurrencyCompact(currentCash)} and the 13-week plan projects ${formatSignedCurrency(projectedDelta)} of net movement.`
              : `Cash is ${formatCurrencyCompact(currentCash)} as of ${asOfLabel}.`,
            actionSummary
              ? `The top modeled cash action is ${actionSummary}.`
              : null,
          ].filter(Boolean),
          instant_card_prescriptive_actions: [
            'Inspect the weeks with the largest cash movement before changing collection, payroll, or vendor timing assumptions.',
          ],
          instant_card_next_steps: [
            {
              kind: 'drilldown',
              label: 'Inspect cash timing',
              prompt: 'Show the weeks and drivers behind cash movement.',
              target: { surface: SURFACE, metricId: metric.id },
            },
            {
              kind: 'open_surface',
              label: 'Open cash planning',
              prompt: 'Open the cash planning workspace.',
              target: { surface: SURFACE, metricId: metric.id },
            },
            {
              kind: 'compare',
              label: 'Compare periods',
              prompt: 'Compare cash across the required periods.',
              target: { surface: SURFACE, metricId: metric.id },
            },
          ],
          deterministic_read:
            projectedDelta != null
              ? `Cash is ${formatCurrencyCompact(currentCash)}; the 13-week plan projects ${formatSignedCurrency(projectedDelta)} of net movement.`
              : `Cash is ${formatCurrencyCompact(currentCash)} as of ${asOfLabel}.`,
        },
      },
      visual: null,
    }),
  ]
}

export function CfoCashPage({
  appApi,
  workerClient = null,
  workbookId,
  selectedEndPeriod = null,
  onSelectedEndPeriodChange,
  periodOptions = [],
  comparisonKind = 'mom',
  onComparisonKindChange,
  comparisonOptions = [],
  onNavSelect,
  onOpenClassic,
  classicEscapes = false,
  navProfile = 'full',
  runtimeProfile,
}: CfoCashPageProps) {
  const [comparisons, setComparisons] =
    useState<MetricComparisonResponse | null>(null)
  const [insights, setInsights] = useState<CfoInsightsResponse | null>(null)
  const [cashPlanning, setCashPlanning] =
    useState<QuickBooksCashPlanningResponse | null>(null)
  const [cashActions, setCashActions] =
    useState<QuickBooksFpaMlCashActionResponse | null>(null)
  const [cashPlanningKey, setCashPlanningKey] = useState('')
  const [historyWeeks, setHistoryWeeks] =
    useState<(typeof CASH_HISTORY_OPTIONS)[number]>(4)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [minCashFloor, setMinCashFloor] = useState<number | null>(null)
  const [selectedCell, setSelectedCell] = useState<{
    rowKey: CashTableCategoryKey
    weekStart: string
    rowIds: string[]
    /** Set when a sub-schedule child was clicked. A child shares its parent's
     * `rowKey`, so without this the re-resolve below finds the PARENT line and
     * silently widens the drill from "payroll taxes" to all of Payroll. */
    childKey?: string
  } | null>(null)
  const [cashMoveSaving, setCashMoveSaving] = useState(false)
  const [cashMoveError, setCashMoveError] = useState('')
  const cashMovePendingRef = useRef(false)
  const cashMoveSequenceRef = useRef(0)
  const demoScenario = isDemoSession() ? demoScenarioKey() : null
  const { bootstrap, cacheState, analyticsReady, bootstrapLoading } =
    useCfoBootstrap(appApi, workbookId)
  const isQuickBooksProvider = !bootstrap?.accounting_provider || bootstrap.accounting_provider === 'quickbooks'
  // The 13-week cash VIEW is Essentials; modeled cash actions are Pro.
  const proFeatures = cfoProFeaturesAvailable(bootstrap)

  const comparisonsRef = useRef<MetricComparisonResponse | null>(null)
  useEffect(() => {
    comparisonsRef.current = comparisons
  }, [comparisons])

  useEffect(() => {
    const controller = new AbortController()
    const wb = trimStr(workbookId)
    if (!wb) {
      setLoading(false)
      setError('No workbook is bound to this view.')
      return
    }
    const resolvedPeriod = trimStr(selectedEndPeriod)
    // The unified surface resolves the saved/default accounting period before
    // cash can query. Fetching with a blank period and repeating the same work
    // after bootstrap was the production two-request regression.
    if (!resolvedPeriod) {
      setLoading(true)
      setError('')
      return () => controller.abort()
    }
    setLoading(true)
    setError('')
    setCashPlanningKey('')
    setComparisons(null)
    setInsights(null)
    setCashActions(null)
    const loadCashPlanning = (appApi.getCfoCashPlanning || appApi.getQuickBooksCashPlanning)?.bind(appApi)
    if (typeof loadCashPlanning !== 'function') {
      setLoading(false)
      setError('The cash-planning reporting contract is not available in this client session.')
      return
    }
    const requestKey = [wb, demoScenario || 'base', resolvedPeriod, historyWeeks, 13].join(':')
    const startedAt = performance.now()
    loadCashPlanning(wb, {
      scenario_key: demoScenario || 'base',
      as_of_date: resolvedPeriod,
      history_weeks: historyWeeks,
      forecast_weeks: 13,
    }, controller.signal)
      .then((planning: QuickBooksCashPlanningResponse) => {
        if (controller.signal.aborted) return
        setCashPlanning(planning)
        setCashPlanningKey(requestKey)
        const seededFloor = planning?.min_cash_floor ?? null
        setMinCashFloor(
          demoScenario ? loadDemoFloor(wb, seededFloor) : seededFloor,
        )
        try {
          performance.measure('cfo.cash.core-ready', {
            start: startedAt,
            end: performance.now(),
            detail: { workbookId: wb, asOf: resolvedPeriod, historyWeeks },
          })
        } catch {
          // Older embedded webviews can lack PerformanceMeasureOptions. The
          // measurement is diagnostic and must never turn valid cash into an
          // error state.
        }
      })
      .catch((err: any) => {
        if (controller.signal.aborted || err?.name === 'AbortError') return
        setError(trimStr(err?.message || err) || 'Could not load cash.')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [appApi, demoScenario, historyWeeks, selectedEndPeriod, workbookId])

  useEffect(() => {
    const serving = cashPlanning?.serving
    if (
      !serving?.status_url ||
      (serving.readiness !== 'materializing' &&
        serving.readiness !== 'stale_ready')
    )
      return
    const wb = trimStr(workbookId)
    const resolvedPeriod = trimStr(selectedEndPeriod)
    if (!wb || !resolvedPeriod) return
    const controller = new AbortController()
    const retryMs = Math.max(
      1_000,
      Math.min(10_000, Number(serving.retry_after_seconds || 2) * 1_000),
    )
    const timer = window.setTimeout(() => {
      const loadCashPlanning = (
        appApi.getCfoCashPlanning || appApi.getQuickBooksCashPlanning
      )?.bind(appApi)
      if (typeof loadCashPlanning !== 'function') return
      void loadCashPlanning(
        wb,
        {
          scenario_key: demoScenario || 'base',
          as_of_date: resolvedPeriod,
          history_weeks: historyWeeks,
          forecast_weeks: 13,
        },
        controller.signal,
      )
        .then((planning: QuickBooksCashPlanningResponse) => {
          if (!controller.signal.aborted) setCashPlanning(planning)
        })
        .catch((err: any) => {
          if (controller.signal.aborted || err?.name === 'AbortError') return
          setError(
            trimStr(err?.message || err) ||
              'Cash is still being prepared. Try again shortly.',
          )
        })
    }, retryMs)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [
    appApi,
    cashPlanning?.serving,
    demoScenario,
    historyWeeks,
    selectedEndPeriod,
    workbookId,
  ])

  useEffect(() => {
    let cancelled = false
    const wb = trimStr(workbookId)
    const resolvedPeriod = trimStr(selectedEndPeriod)
    const expectedKey = [wb, demoScenario || 'base', resolvedPeriod, historyWeeks, 13].join(':')
    if (!wb || !resolvedPeriod || cashPlanningKey !== expectedKey) return

    const loadAnalyticsSidecars = analyticsReady === true
    const loadComparisons = (appApi.getCfoMetricComparisons || appApi.getQuickBooksMetricComparisons)?.bind(appApi)
    const loadInsights = (appApi.getCfoInsights || appApi.getQuickBooksInsights)?.bind(appApi)
    void Promise.all([
      loadAnalyticsSidecars && typeof loadComparisons === 'function'
        ? loadComparisons(wb, {
            asOf: resolvedPeriod,
            grain: 'month',
            metrics: ['cash', 'ending_cash'],
            comparisons: CASH_REQUIRED_COMPARISONS,
          }).catch(() => null)
        : Promise.resolve(null),
      loadAnalyticsSidecars && typeof loadInsights === 'function'
        ? loadInsights(wb, {
            asOf: resolvedPeriod,
            grain: 'month',
          }).catch(() => null)
        : Promise.resolve(null),
      proFeatures && isQuickBooksProvider
        ? appApi.getQuickBooksFpaMlCashActions(wb, { limit: 8 }).catch(() => null)
        : Promise.resolve(null),
    ]).then(([cmp, ins, actions]) => {
      if (cancelled) return
      setComparisons(cmp)
      setInsights(ins)
      setCashActions(actions)
    })
    return () => {
      cancelled = true
    }
  }, [
    analyticsReady,
    appApi,
    cashPlanningKey,
    demoScenario,
    historyWeeks,
    isQuickBooksProvider,
    proFeatures,
    selectedEndPeriod,
    workbookId,
  ])

  useEffect(() => {
    const canonicalAsOf = trimStr(insights?.as_of || comparisons?.as_of || '')
    if (!selectedEndPeriod && canonicalAsOf)
      onSelectedEndPeriodChange?.(canonicalAsOf)
  }, [comparisons, insights, onSelectedEndPeriodChange, selectedEndPeriod])

  const asOfLabel = useMemo(
    () =>
      formatAsOf(
        selectedEndPeriod || insights?.as_of || comparisons?.as_of || null,
      ),
    [selectedEndPeriod, insights, comparisons],
  )
  const cashCards = useMemo<InsightCard[]>(
    () =>
      (insights?.cards || []).filter(
        (card) => card.drill_target.tab === 'cash',
      ),
    [insights],
  )
  const duckDbOpts = useMemo(() => buildCfoDuckDbOpts(workbookId), [workbookId])
  const { openGlDetail, glDetailModal } = useCfoInlineGlDetail({
    appApi,
    workerClient,
    workbookId,
    duckDbOpts,
    onNavSelect,
    period: selectedEndPeriod || comparisons?.as_of || insights?.as_of || null,
  })

  const planningTrend = useMemo(
    () => trendFromCashPlanningWeeks(cashPlanning?.weeks),
    [cashPlanning],
  )
  const cashMetric =
    findMetric(comparisons, 'cash') || findMetric(comparisons, 'ending_cash')
  const currentCash =
    cashMetric?.latest_value ?? planningTrend.openingCash ?? null

  // Use the governed cash-planning contract when available. The legacy cash
  // week shape is intentionally not fetched for the common page render.
  const trend = useMemo<{
    points: TrendPoint[]
    projectedDelta: number | null
  }>(() => {
    if (planningTrend.points.length >= 2) {
      return {
        points: planningTrend.points,
        projectedDelta: planningTrend.projectedDelta,
      }
    }
    return { points: [], projectedDelta: null }
  }, [planningTrend])
  const cashInstantCards = useMemo(
    () =>
      buildCashInstantCards({
        comparisons,
        cashPlanning,
        cashActions,
        planningTrend: trend,
        asOfLabel,
        comparisonKind,
      }),
    [asOfLabel, cashActions, cashPlanning, comparisonKind, comparisons, trend],
  )
  const cashPageFactsFingerprint = useMemo(
    () => cashSourceFingerprint(comparisons, cashPlanning),
    [cashPlanning, comparisons],
  )

  const cashTableModel = useMemo(
    () => buildCashTableModel(cashPlanning, minCashFloor),
    [cashPlanning, minCashFloor],
  )
  const troughCallouts = useMemo(
    () => buildTroughCallouts(cashTableModel, cashPlanning?.rows || []),
    [cashPlanning, cashTableModel],
  )
  // Real edit access, server-derived. Absent on payloads served by an older
  // build, so default permissive: the write endpoints enforce this themselves,
  // and locking the UI on a missing field would be a worse failure than
  // showing a control that 403s.
  const canEditCash = cashPlanning?.permissions?.can_edit !== false
  const cashCurrencyPresentation = cfoCurrencyPresentation()
  const [addingItem, setAddingItem] = useState(false)
  const [addItemSaving, setAddItemSaving] = useState(false)
  const [addItemError, setAddItemError] = useState('')
  // Only open weeks are move targets — closed weeks are posted actuals, and
  // re-timing into one would rewrite history.
  const forecastWeekOptions = useMemo(
    () =>
      cashTableModel.columns
        .filter((column) => column.status !== 'closed')
        .map((column) => ({ weekStart: column.weekStart, label: column.label })),
    [cashTableModel],
  )
  const rowsById = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<typeof cashPlanning>['rows'][number]
    >()
    for (const row of cashPlanning?.rows || []) map.set(String(row.id), row)
    return map
  }, [cashPlanning])
  // Re-resolve the open cell's rows from the REBUILT table model rather than the
  // ids captured when it was clicked. A move mints a new row id
  // (local-cash-move:N, then the persisted uuid) and re-buckets the row into a
  // different week, so a captured id list would silently render a stale — and
  // after reconcile, empty — panel.
  const selectedCellRows = useMemo(() => {
    if (!selectedCell) return []
    const columnIndex = cashTableModel.columns.findIndex(
      (col) => col.weekStart === selectedCell.weekStart,
    )
    // Match the child too, not just the category: `lines` now holds a parent and
    // its children under the SAME `key`, and the parent comes first.
    const line = cashTableModel.lines.find(
      (entry) =>
        entry.key === selectedCell.rowKey &&
        (entry.childKey ?? undefined) === (selectedCell.childKey ?? undefined),
    )
    const ids =
      columnIndex >= 0 && line?.cells[columnIndex]
        ? line.cells[columnIndex].rowIds
        : selectedCell.rowIds
    return ids
      .map((id) => rowsById.get(id))
      .filter((row): row is NonNullable<typeof row> => Boolean(row))
  }, [cashTableModel, rowsById, selectedCell])
  const selectedCellDetailHandle = useMemo(() => {
    if (!selectedCell) return null
    // Detail handles are served per CATEGORY. Handing the parent's handle to a
    // child selection would quietly reopen the whole category behind a heading
    // that says "Payroll taxes" — the drill would name one thing and show
    // another. A child drills its own rows and nothing else.
    if (selectedCell.childKey) return null
    return (
      cashPlanning?.serving?.detail_handles?.find(
        (handle) =>
          handle.week_start === selectedCell.weekStart &&
          handle.category_id === selectedCell.rowKey,
      ) || null
    )
  }, [cashPlanning?.serving?.detail_handles, selectedCell])
  const moveCashItem = async (move: CashPlanningMove) => {
    const wb = trimStr(workbookId)
    const snapshot = cashPlanning
    if (!wb || !snapshot) throw new Error('Cash planning is not ready yet.')
    if (cashMovePendingRef.current)
      throw new Error('Finish saving the current cash move first.')

    cashMovePendingRef.current = true
    cashMoveSequenceRef.current += 1
    const optimisticId = `local-cash-move:${cashMoveSequenceRef.current}`
    const optimisticRow = optimisticExpectationRow(
      snapshot,
      move,
      optimisticId,
    )
    const optimisticPlan = projectCashPlanningExpectation(
      snapshot,
      optimisticRow,
      move.expectationId,
    )
    const previousSelection = selectedCell
    setCashMoveError('')
    setCashMoveSaving(true)
    setCashPlanning(optimisticPlan)
    // The panel deliberately stays open: the drill re-resolves its rows from the
    // rebuilt table model each render, so the moved item leaves this cell and
    // the totals update in place. Closing it here used to make a move feel like
    // the item had vanished with no feedback.

    try {
      let persisted: QuickBooksCashPlanningRow
      if (move.expectationId) {
        persisted = await appApi.updateQuickBooksCashPlanningExpectation(
          wb,
          move.expectationId,
          { expected_date: move.targetDate },
        )
      } else {
        persisted = await appApi.createQuickBooksCashPlanningExpectation(wb, {
          scenario_key: move.scenarioKey,
          event_type: move.eventType,
          counterparty_name: move.counterparty,
          description: `${move.title} — moved from ${move.fromDate}`,
          expected_date: move.targetDate,
          expected_amount: Math.abs(move.amount),
          source_type: expectationSourceTypeFor(
            move.sourceType,
            move.rowKey,
            move.provenance,
          ),
          source_id: move.documentId,
          // A recurring claim anchors on the occurrence being moved, not the
          // target — see `cash_claim_from_expectation`.
          source_payload_json:
            move.provenance === 'recurring'
              ? { claim: { occurrence: move.fromDate } }
              : undefined,
          note: 'Moved from the 13-week cash table',
        })
      }
      setCashPlanning((current) => {
        if (current !== optimisticPlan) return current
        const reconciled = reconcileCashPlanningExpectation(
          optimisticPlan,
          optimisticId,
          persisted,
        )
        return persisted.overlay_revision != null && reconciled.serving
          ? {
              ...reconciled,
              serving: {
                ...reconciled.serving,
                overlay_revision: persisted.overlay_revision,
              },
            }
          : reconciled
      })
    } catch (err: any) {
      setCashPlanning((current) =>
        current === optimisticPlan ? snapshot : current,
      )
      setSelectedCell(previousSelection)
      const message =
        Number(err?.status) === 403
          ? 'Read-only session — try this on your own books →'
          : trimStr(err?.body || err?.message || err) ||
            'Could not save the cash move.'
      setCashMoveError(message)
      throw err
    } finally {
      cashMovePendingRef.current = false
      setCashMoveSaving(false)
    }
  }
  const resetCashItem = async (expectationId: string) => {
    const wb = trimStr(workbookId)
    if (!wb) return
    await appApi.deleteQuickBooksCashPlanningExpectation(wb, expectationId)
    // Refetch rather than project: dropping an expectation un-suppresses the
    // model rows it was replacing, and only the server knows what those were.
    const loadCashPlanning = (appApi.getCfoCashPlanning || appApi.getQuickBooksCashPlanning)?.bind(appApi)
    if (typeof loadCashPlanning !== 'function') throw new Error('Cash-planning reporting contract unavailable')
    const refreshed = await loadCashPlanning(wb, {
      scenario_key: cashPlanning?.scenario_key || demoScenario || 'base',
      as_of_date: selectedEndPeriod,
      history_weeks: historyWeeks,
      forecast_weeks: 13,
    })
    setCashPlanning(refreshed)
  }
  /**
   * Key this week's bank balance, then REFETCH rather than patch state.
   *
   * The tie-out is computed server-side against the governed anchor and the
   * day-grain posted actuals. Projecting a new float on the client would need a
   * second copy of that arithmetic, and a second copy is a copy that drifts.
   */
  const keyBankBalance = async (balanceDate: string, bankBalance: number) => {
    const wb = trimStr(workbookId)
    if (!wb) throw new Error('No workbook is bound to this view.')
    const saved = await appApi.putCashBankObservation(wb, {
      balance_date: balanceDate,
      bank_balance: bankBalance,
    })
    // The server answers 200 with `read_only_demo` for a demo session rather
    // than 403 — deliberate, so a demo gets a state it can act on. But nothing
    // read that status, so the form closed, the page refetched, and the strip
    // came back unchanged: a save that reported success and stored nothing.
    // Say so instead.
    if (saved?.status === 'read_only_demo') {
      throw new Error(
        'This is a read-only demo, so the balance was not saved. Connect your own books to reconcile.',
      )
    }
    const loadCashPlanning = (appApi.getCfoCashPlanning || appApi.getQuickBooksCashPlanning)?.bind(appApi)
    if (typeof loadCashPlanning !== 'function') throw new Error('Cash-planning reporting contract unavailable')
    const refreshed = await loadCashPlanning(wb, {
      scenario_key: cashPlanning?.scenario_key || demoScenario || 'base',
      as_of_date: selectedEndPeriod,
      history_weeks: historyWeeks,
      forecast_weeks: 13,
    })
    setCashPlanning(refreshed)
  }
  const addCashItem = async (value: CfoCashAddItemValue) => {
    const wb = trimStr(workbookId)
    const snapshot = cashPlanning
    if (!wb || !snapshot) return
    setAddItemSaving(true)
    setAddItemError('')
    try {
      const persisted = await appApi.createQuickBooksCashPlanningExpectation(wb, {
        scenario_key: cashPlanning?.scenario_key || demoScenario || 'base',
        treasurer_row_key: value.treasurerRowKey,
        direction: value.direction,
        counterparty_name: value.counterparty || null,
        description: value.description,
        expected_date: value.expectedDate,
        expected_amount: value.amount,
        source_type: 'user_cash_expectation',
        note: 'Added from the 13-week cash plan',
      })
      // Project the persisted row rather than an optimistic guess: the server
      // decides the event_type from (category, direction), and that string is
      // what places the row and signs it.
      setCashPlanning((current) => {
        if (!current) return current
        const projected = projectCashPlanningExpectation(current, persisted, null)
        return persisted.overlay_revision != null && projected.serving
          ? {
              ...projected,
              serving: {
                ...projected.serving,
                overlay_revision: persisted.overlay_revision,
              },
            }
          : projected
      })
      setAddingItem(false)
    } catch (err: any) {
      setAddItemError(
        Number(err?.status) === 403
          ? 'Read-only session — try this on your own books →'
          : trimStr(err?.body || err?.message || err) || 'Could not add this item.',
      )
    } finally {
      setAddItemSaving(false)
    }
  }
  const handleFloorChange = (floor: number | null) => {
    setMinCashFloor(floor)
    const wb = trimStr(workbookId)
    if (!wb) return
    if (demoScenario) {
      try {
        window.localStorage.setItem(
          demoFloorStorageKey(wb),
          floor == null ? 'null' : String(floor),
        )
      } catch {
        // The table still updates for this page session.
      }
      return
    }
    appApi
      .setCfoDisplayPrefs(
        wb,
        floor == null
          ? { clear_min_cash_floor: true }
          : { min_cash_floor: floor },
      )
      .catch(() => {})
  }

  const heroHeadline =
    currentCash != null
      ? `Cash on hand is ${formatCurrencyCompact(currentCash)}`
      : 'Your cash position'
  const heroSubtext = troughCallouts.length
    ? `${troughCallouts.length === 1 ? 'One week' : `${troughCallouts.length} weeks`} in the next 13 dips below your minimum-cash floor — the week and the cause are called out below.`
    : trend.projectedDelta != null
      ? `Over the next 13 weeks the forecast projects a net change of ${formatSignedCurrency(trend.projectedDelta)}.`
      : 'Week-by-week cash for the next 13 weeks — collections, payroll, rent, and the rest, with the tight weeks flagged.'

  const axisLabels = useMemo(() => {
    const pts = trend.points
    if (pts.length < 2) return undefined
    return ['Now', 'Wk 4', 'Wk 8', 'Wk 13']
  }, [trend.points])

  return (
    <CfoInteriorPage
      appApi={appApi}
      workerClient={workerClient}
      workbookId={workbookId}
      navId="cash"
      activeSection={SURFACE}
      eyebrow="Understand"
      title="Cash"
      lede={
        <>
          “Where cash stands today, and where the next 13 weeks take it.{' '}
          <em>The plan is one tap down.</em>”
        </>
      }
      crumb={
        <>
          <b>Cash</b> · Position &amp; 13-week
        </>
      }
      asOfLabel={asOfLabel}
      periodOptions={periodOptions}
      periodValue={
        selectedEndPeriod || comparisons?.as_of || insights?.as_of || undefined
      }
      onPeriodChange={onSelectedEndPeriodChange}
      periodAriaLabel="Select CFO Core end period"
      comparisonLabel={
        comparisonOptions.find((option) => option.id === comparisonKind)
          ?.label || 'vs. prior month'
      }
      comparisonOptions={comparisonOptions}
      comparisonValue={comparisonKind}
      onComparisonChange={(value) =>
        onComparisonKindChange?.(value as MetricComparisonKind)
      }
      comparisonAriaLabel="Select CFO Core comparison period"
      cacheState={cacheState}
      pageLoading={loading || bootstrapLoading}
      pageLoadingLabel="Loading cash context…"
      copilotSubtitle="reading your cash position"
      copilotPlaceholder="Ask about Cash…"
      copilotIntro="You’re on Cash. Ask me about runway, the 13-week forecast, or what’s driving a week."
      askPlaceholder="Ask about cash…"
      suggestedPrompts={[
        'How is cash trending?',
        'What’s my runway?',
        'Which weeks look tight?',
      ]}
      getComparisons={() => comparisonsRef.current}
      instantCards={cashInstantCards}
      pageFactsFingerprint={cashPageFactsFingerprint}
      openGlDetail={openGlDetail}
      duckDbOpts={duckDbOpts}
      onNavSelect={onNavSelect}
      navProfile={navProfile}
      runtimeProfile={runtimeProfile}
    >
      {({ copilot }) => (
        <>
          {glDetailModal}
          <CfoControlRow>
            <label
              className="cfo-shell-chip"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span>Actual</span>
              <select
                aria-label="Cash history weeks"
                value={historyWeeks}
                onChange={(event) =>
                  setHistoryWeeks(
                    Number(
                      event.target.value,
                    ) as (typeof CASH_HISTORY_OPTIONS)[number],
                  )
                }
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'inherit',
                  font: 'inherit',
                  minWidth: 44,
                }}
              >
                {CASH_HISTORY_OPTIONS.map((weeks) => (
                  <option key={weeks} value={weeks}>
                    {weeks} weeks
                  </option>
                ))}
              </select>
              <span style={{ color: 'var(--muted)' }}>
                | Forecast: 13 weeks
              </span>
            </label>
          </CfoControlRow>

          <section className="hero rise">
            <div className="hero-top">
              <div>
                <h1>{heroHeadline}</h1>
                <p>{heroSubtext}</p>
                <span className="tag">Based on your live 13-week plan</span>
              </div>
            </div>
            <figure className="figure">
              <div className="fig-head">
                <span className="fnum">Fig. 1</span>
                <span className="ftitle">The 13-week cash plan</span>
                <span className="fmeta">
                  weeks across the top · cash categories down the side ·{' '}
                  {cashCurrencyPresentation.note}
                </span>
              </div>
              {cashPlanning?.serving?.stale ? (
                <div className="cfo-shell-notice" role="status">
                  Showing the last ready cash plan for this date while the new
                  source revision is prepared.
                </div>
              ) : null}
              {loading ? (
                <CfoChartSkeleton label="Loading the 13-week cash plan…" />
              ) : cashPlanning?.serving?.readiness === 'materializing' ? (
                <div className="cfo-shell-notice" role="status">
                  <strong>Preparing cash for this date</strong>
                  <p style={{ margin: '6px 0 0' }}>
                    The calculation is continuing in the background. You can
                    use the rest of the workbook while this view refreshes.
                  </p>
                </div>
              ) : cashPlanning?.cash_position?.status === 'blocking' ? (
                <div
                  className="cfo-shell-notice error"
                  role="alert"
                  data-testid="cash-data-quality-block"
                >
                  <strong>Cash forecast is blocked</strong>
                  <p style={{ margin: '6px 0 0' }}>
                    {cashPlanning.cash_position.message}
                  </p>
                  <p style={{ margin: '6px 0 0', color: 'var(--muted)' }}>
                    This forecast will not start from $0. Reconcile the
                    balance-sheet cash balance, then refresh this page.
                  </p>
                </div>
              ) : cashTableModel.columns.length ? (
                <>
                  {cashMoveSaving ? (
                    <div className="cfo-shell-notice" role="status">
                      Move applied · saving in the background…
                    </div>
                  ) : null}
                  {cashMoveError ? (
                    <div className="cfo-shell-notice error" role="alert">
                      {cashMoveError}
                    </div>
                  ) : null}
                  <CfoCashAvailableStrip
                    planning={cashPlanning}
                    currency={formatCurrencyCompact}
                  />
                  <CfoCashTieOutStrip
                    tieOut={cashPlanning?.cash_tie_out}
                    canEdit={canEditCash}
                    currency={formatCurrencyCompact}
                    onKeyBalance={keyBankBalance}
                  />
                  <Cfo13WeekCashTable
                    model={cashTableModel}
                    troughs={troughCallouts}
                    minCashFloor={minCashFloor}
                    currencyDisplay={cashCurrencyPresentation.display}
                    canEditFloor={canEditCash}
                    onFloorChange={handleFloorChange}
                    onCellSelect={(rowKey, weekStart, rowIds, childKey) =>
                      setSelectedCell((current) =>
                        current &&
                        current.rowKey === rowKey &&
                        (current.childKey ?? undefined) ===
                          (childKey ?? undefined) &&
                        current.weekStart === weekStart
                          ? null
                          : { rowKey, weekStart, rowIds, childKey },
                      )
                    }
                    selectedCell={selectedCell}
                  />
                  {selectedCell ? (
                    <CfoCashWeekDrill
                      appApi={appApi}
                      workbookId={workbookId}
                      rowKey={selectedCell.rowKey}
                      rowLabel={
                        cashTableModel.lines.find(
                          (line) =>
                            line.key === selectedCell.rowKey &&
                            (line.childKey ?? undefined) ===
                              (selectedCell.childKey ?? undefined),
                        )?.label || selectedCell.rowKey
                      }
                      weekStart={selectedCell.weekStart}
                      weekLabel={
                        cashTableModel.columns.find(
                          (col) => col.weekStart === selectedCell.weekStart,
                        )?.label || selectedCell.weekStart
                      }
                      rows={selectedCellRows}
                      allRows={cashPlanning?.rows || []}
                      weekOptions={forecastWeekOptions}
                      canEdit={canEditCash}
                      scenarioKey={cashPlanning?.scenario_key || demoScenario || 'base'}
                      summaryId={cashPlanning?.serving?.summary_id}
                      detailHandle={selectedCellDetailHandle}
                      onMove={moveCashItem}
                      onReset={resetCashItem}
                      onClose={() => setSelectedCell(null)}
                    />
                  ) : null}
                  {canEditCash ? (
                    addingItem ? (
                      <CfoCashAddItem
                        weekOptions={forecastWeekOptions}
                        defaultRowKey={selectedCell?.rowKey}
                        defaultDate={
                          selectedCell?.weekStart || forecastWeekOptions[0]?.weekStart
                        }
                        busy={addItemSaving}
                        error={addItemError}
                        onSubmit={addCashItem}
                        onCancel={() => {
                          setAddingItem(false)
                          setAddItemError('')
                        }}
                      />
                    ) : (
                      <div style={{ marginTop: 10 }}>
                        <button
                          type="button"
                          className="cfo-shell-chip"
                          onClick={() => setAddingItem(true)}
                        >
                          + Add something you know about
                        </button>
                      </div>
                    )
                  ) : null}
                </>
              ) : (
                <p style={{ color: 'var(--muted)' }}>
                  The 13-week cash plan isn’t available for this workbook yet.
                </p>
              )}
              <CfoAiRead>
                Cash risk usually appears as timing: collections slipping,
                payroll/rent clustering, or one large expected inflow moving
                out. Click a cell to see the items behind it — and move one to
                see the plan recompute.
              </CfoAiRead>
            </figure>
            {trend.points.length >= 2 ? (
              <figure className="figure" style={{ marginTop: 12 }}>
                <div className="fig-head">
                  <span className="fnum">Fig. 2</span>
                  <span className="ftitle">
                    Projected ending cash · next 13 weeks
                  </span>
                </div>
                <TrendLine
                  className="fig-svg"
                  ariaLabel="Projected ending cash over the next thirteen weeks"
                  points={trend.points}
                  axisLabels={axisLabels}
                  formatValue={(v) => formatCurrencyCompact(v)}
                />
              </figure>
            ) : null}
          </section>

          <div className="section-title rise">
            <h2 className="ff-guide">Short-term cash actions</h2>
            <span className="meta">from the forecast model</span>
          </div>
          {!proFeatures ? (
            <article
              className="cfo-shell-notice"
              style={{ marginBottom: 'var(--s4)' }}
            >
              <strong>Modeled cash actions are part of Pro</strong>
              <p style={{ margin: '6px 0', color: 'var(--muted)' }}>
                Pro suggests concrete moves — which receivables to chase, which
                payments to shift — each with its modeled cash impact on this
                forecast.
              </p>
              <button
                type="button"
                className="btn ghost"
                onClick={() => {
                  const wb = trimStr(workbookId)
                  if (wb)
                    startCfoCheckout(appApi, wb, 'pro', 'monthly').catch(
                      () => {},
                    )
                }}
              >
                Upgrade to Pro — $349/mo
              </button>
            </article>
          ) : cashActions?.actions?.length ? (
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                marginBottom: 'var(--s4)',
              }}
            >
              {cashActions.actions.slice(0, 6).map((action) => (
                <article key={action.id} className="cfo-shell-notice">
                  <strong>{action.title}</strong>
                  <p style={{ margin: '6px 0', color: 'var(--muted)' }}>
                    {action.description}
                  </p>
                  <span className="tag">
                    {formatCurrencyCompact(
                      Number(action.expected_cash_impact) || 0,
                    )}{' '}
                    modeled impact
                  </span>
                </article>
              ))}
            </div>
          ) : null}
          <CfoInsightCards
            cards={cashCards}
            loading={loading}
            error={error}
            copilot={copilot}
            surface={SURFACE}
            workbookId={workbookId}
          />

          {classicEscapes ? (
            <button
              type="button"
              className="btn ghost"
              style={{ marginTop: 'var(--s4)' }}
              onClick={onOpenClassic}
            >
              Open cash planning
            </button>
          ) : null}
        </>
      )}
    </CfoInteriorPage>
  )
}

export default CfoCashPage
